import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Store as StoreIcon,
  Users as UsersIcon,
  Boxes,
  Trash2,
  Inbox,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import EmptyState from "../../components/system/EmptyState";
import ClickableStatCard from "../../components/system/ClickableStatCard";
import UserStatusBadge from "../../components/system/UserStatusBadge";
import {
  OperatorTable,
  OperatorTableRow,
} from "../../components/system/OperatorTable";

/**
 * OperatorStoreDetailPage — `/platform/companies/:cid/stores/:storeId`
 *
 * Per-store roll-up for the operator console. Shows:
 *   - Store metadata (name, code, id, active flag)
 *   - User counts (total/active/inactive) + read-only users-in-store table
 *   - Departments-in-use chips
 *   - Click-through cards to filtered Batches / Removals / Product Requests
 *     scoped to this store
 *
 * Phase 1: READ-ONLY. No actions on per-store users.
 */
export default function OperatorStoreDetailPage() {
  const { cid, storeId } = useParams();
  const [companyName, setCompanyName] = useState("");
  const [summary, setSummary] = useState(null);
  const [storeUsers, setStoreUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [det, sum, us, dp] = await Promise.all([
        api.get(`/platform/companies/${cid}`),
        api.get(`/platform/companies/${cid}/stores/${storeId}/summary`),
        api.get(`/platform/companies/${cid}/users`),
        api.get(`/platform/companies/${cid}/departments`),
      ]);
      setCompanyName(det.data.company?.name || "");
      setSummary(sum.data);
      setStoreUsers(
        (us.data.users || []).filter((u) => u.store_id === storeId),
      );
      setDepartments(dp.data.departments || []);
    } catch (e) {
      const msg = formatApiErrorDetail(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [cid, storeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const deptName = useMemo(() => {
    const map = new Map(departments.map((d) => [d.id, d.name]));
    return (id) => (id ? map.get(id) || "—" : "—");
  }, [departments]);

  if (loading) {
    return (
      <div data-testid="operator-store-detail-loading">
        <Card variant="raised" className="p-10 text-center">
          <div className="font-mono text-sm text-muted-foreground">
            Loading store…
          </div>
        </Card>
      </div>
    );
  }

  if (error || !summary?.store) {
    return (
      <div data-testid="operator-store-detail-error">
        <Link
          to={`/platform/companies/${cid}/stores`}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-ink mb-4"
        >
          <ArrowLeft size={14} />
          Back to stores
        </Link>
        <EmptyState
          icon={<StoreIcon size={36} />}
          title="Store not found"
          description={error || "We could not load this store."}
        />
      </div>
    );
  }

  const s = summary.store;
  const u = summary.users || {};
  const GRID = "minmax(0,2fr) 1fr 1fr 1fr 1fr";

  return (
    <div data-testid="operator-store-detail-page">
      <div className="mb-4">
        <Link
          to={`/platform/companies/${cid}/stores`}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-ink"
          data-testid="operator-store-detail-back"
        >
          <ArrowLeft size={14} />
          Back to stores
        </Link>
      </div>

      <PageHeader
        eyebrow={
          <span className="font-mono text-[11px]">
            Tenant · {companyName || cid} · code{" "}
            <span data-testid="operator-store-detail-code">
              {s.code || "—"}
            </span>{" "}
            · id{" "}
            <span data-testid="operator-store-detail-id">{s.id}</span>
          </span>
        }
        title={s.name}
        subtitle={
          <>
            Read-only roll-up. To act on a user (mint reset link, etc.), open{" "}
            <Link
              to={`/platform/companies/${cid}/users`}
              className="text-ink underline"
              data-testid="operator-store-detail-link-users"
            >
              Total users
            </Link>{" "}
            for this tenant.
          </>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <ClickableStatCard
          label="Total users"
          value={u.total}
          to={`/platform/companies/${cid}/users?store=${storeId}`}
          testid="operator-store-card-total-users"
        />
        <ClickableStatCard
          label="Active users"
          value={u.active}
          to={`/platform/companies/${cid}/users?store=${storeId}&status=active`}
          testid="operator-store-card-active-users"
        />
        <ClickableStatCard
          label="Inactive users"
          value={u.inactive}
          to={`/platform/companies/${cid}/users?store=${storeId}&status=inactive`}
          testid="operator-store-card-inactive-users"
        />
        <ClickableStatCard
          label="Active batches"
          value={summary.active_batches}
          to={`/platform/companies/${cid}/batches?status=active&store=${storeId}`}
          testid="operator-store-card-active-batches"
        />
        <ClickableStatCard
          label="Removals (30d)"
          value={summary.removals_30d}
          to={`/platform/companies/${cid}/removals?days=30&store=${storeId}`}
          testid="operator-store-card-removals"
        />
        <ClickableStatCard
          label="Open product requests"
          value={summary.pending_product_requests}
          to={`/platform/companies/${cid}/product-requests?status=pending&store=${storeId}`}
          testid="operator-store-card-pending-prs"
        />
      </div>

      {/* Departments used in this store */}
      <section className="mb-6" data-testid="operator-store-departments-section">
        <h2 className="font-display font-black text-xl mb-3">
          Departments in use
        </h2>
        {summary.departments_in_use?.length ? (
          <Card variant="raised" className="p-4">
            <div className="flex flex-wrap gap-2">
              {summary.departments_in_use.map((d) => (
                <Link
                  key={d.id}
                  to={`/platform/companies/${cid}/departments/${d.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-paper-sunk rounded-button font-mono text-xs hover:bg-paper-2 hover:shadow-rest transition-all"
                  data-testid={`operator-store-dept-chip-${d.id}`}
                >
                  <Boxes size={12} />
                  {d.name}
                </Link>
              ))}
            </div>
          </Card>
        ) : (
          <Card variant="flat" className="p-6 text-center">
            <p className="font-mono text-sm text-muted-foreground">
              No departments are currently in use by this store.
            </p>
          </Card>
        )}
      </section>

      {/* Users in this store (read-only) */}
      <section className="mb-6" data-testid="operator-store-users-section">
        <div className="flex items-center justify-between mb-3 gap-2">
          <h2 className="font-display font-black text-xl">
            Users in this store ({storeUsers.length})
          </h2>
          <Link
            to={`/platform/companies/${cid}/users?store=${storeId}`}
            className="font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-ink"
            data-testid="operator-store-users-view-all"
          >
            View all in Total users →
          </Link>
        </div>
        <OperatorTable
          gridTemplate={GRID}
          header={
            <>
              <div>Name</div>
              <div>Role</div>
              <div>Department</div>
              <div>Status</div>
              <div>Created</div>
            </>
          }
          empty={storeUsers.length === 0}
          emptyState={
            <div className="p-10">
              <EmptyState
                icon={<UsersIcon size={28} />}
                title="No users assigned to this store"
                description="Users gain a store assignment via the tenant admin UI."
                data-testid="operator-store-users-empty"
              />
            </div>
          }
          data-testid="operator-store-users-table"
        >
          {storeUsers.map((su) => (
            <OperatorTableRow
              key={su.id}
              gridTemplate={GRID}
              data-testid={`operator-store-user-row-${su.id}`}
            >
              <div className="min-w-0">
                <div className="font-display font-semibold truncate">
                  {su.name || su.email}
                </div>
                <div className="font-mono text-[11px] text-muted-foreground truncate">
                  {su.email}
                </div>
              </div>
              <div className="font-mono text-xs uppercase">{su.role}</div>
              <div className="font-mono text-xs truncate">
                {deptName(su.department_id)}
              </div>
              <div>
                <UserStatusBadge
                  active={!!su.active}
                  data-testid={`operator-store-user-status-${su.id}`}
                />
              </div>
              <div className="font-mono text-[11px] text-muted-foreground">
                {(su.created_at || "").slice(0, 10) || "—"}
              </div>
            </OperatorTableRow>
          ))}
        </OperatorTable>
      </section>

      <Card variant="flat" className="p-4 text-center">
        <p className="font-mono text-[11px] text-muted-foreground inline-flex items-center justify-center gap-2">
          <Inbox size={12} />
          Phase 1: per-store read-only view. Reset / activate / edit actions
          stay in the tenant-wide Total users page.
          {" "}
          {summary.removals_30d > 0 && (
            <>
              <Trash2 size={12} />
              {summary.removals_30d} removal{summary.removals_30d === 1 ? "" : "s"} in the last 30 days.
            </>
          )}
        </p>
      </Card>
    </div>
  );
}
